/**
 * SiteHistoryTimeline Component
 * Displays the history/audit trail of site changes
 */

import React, { useState } from 'react';
import {
  SITE_HISTORY_CHANGE_TYPE,
  SITE_HISTORY_CHANGE_TYPE_LABELS
} from '../../utils/constants';

// Get icon for change type
const getChangeTypeIcon = (changeType) => {
  const icons = {
    [SITE_HISTORY_CHANGE_TYPE.SITE_CREATED]: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
      </svg>
    ),
    [SITE_HISTORY_CHANGE_TYPE.STATUS_CHANGED]: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
      </svg>
    ),
    [SITE_HISTORY_CHANGE_TYPE.PHASE_STARTED]: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
      </svg>
    ),
    [SITE_HISTORY_CHANGE_TYPE.PHASE_COMPLETED]: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    [SITE_HISTORY_CHANGE_TYPE.PHASE_SKIPPED]: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
      </svg>
    ),
    [SITE_HISTORY_CHANGE_TYPE.PHASE_ADDED]: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    [SITE_HISTORY_CHANGE_TYPE.PHASE_REMOVED]: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    [SITE_HISTORY_CHANGE_TYPE.PHASE_PROGRESS_UPDATED]: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
      </svg>
    ),
    [SITE_HISTORY_CHANGE_TYPE.TASK_CREATED]: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
    ),
    [SITE_HISTORY_CHANGE_TYPE.TASK_COMPLETED]: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    ),
    [SITE_HISTORY_CHANGE_TYPE.TASK_DELETED]: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
      </svg>
    )
  };

  return icons[changeType] || (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
};

// Get color for change type
const getChangeTypeColor = (changeType) => {
  const colors = {
    [SITE_HISTORY_CHANGE_TYPE.SITE_CREATED]: 'bg-purple-100 text-purple-600',
    [SITE_HISTORY_CHANGE_TYPE.STATUS_CHANGED]: 'bg-blue-100 text-blue-600',
    [SITE_HISTORY_CHANGE_TYPE.PHASE_STARTED]: 'bg-blue-100 text-blue-600',
    [SITE_HISTORY_CHANGE_TYPE.PHASE_COMPLETED]: 'bg-green-100 text-green-600',
    [SITE_HISTORY_CHANGE_TYPE.PHASE_SKIPPED]: 'bg-yellow-100 text-yellow-600',
    [SITE_HISTORY_CHANGE_TYPE.PHASE_ADDED]: 'bg-indigo-100 text-indigo-600',
    [SITE_HISTORY_CHANGE_TYPE.PHASE_REMOVED]: 'bg-red-100 text-red-600',
    [SITE_HISTORY_CHANGE_TYPE.PHASE_REORDERED]: 'bg-gray-100 text-gray-600',
    [SITE_HISTORY_CHANGE_TYPE.PHASE_WEIGHT_UPDATED]: 'bg-orange-100 text-orange-600',
    [SITE_HISTORY_CHANGE_TYPE.PHASE_PROGRESS_UPDATED]: 'bg-cyan-100 text-cyan-600',
    [SITE_HISTORY_CHANGE_TYPE.TASK_CREATED]: 'bg-teal-100 text-teal-600',
    [SITE_HISTORY_CHANGE_TYPE.TASK_COMPLETED]: 'bg-green-100 text-green-600',
    [SITE_HISTORY_CHANGE_TYPE.TASK_DELETED]: 'bg-red-100 text-red-600',
    [SITE_HISTORY_CHANGE_TYPE.NOTES_UPDATED]: 'bg-gray-100 text-gray-600'
  };

  return colors[changeType] || 'bg-gray-100 text-gray-600';
};

// Format the description based on change type and metadata
const formatDescription = (item) => {
  const { changeType, oldValue, newValue, metadata } = item;

  switch (changeType) {
    case SITE_HISTORY_CHANGE_TYPE.STATUS_CHANGED:
      return `Status changed from "${oldValue}" to "${newValue}"`;
    case SITE_HISTORY_CHANGE_TYPE.PHASE_STARTED:
      return `Started phase: ${metadata?.phaseName || 'Unknown'}`;
    case SITE_HISTORY_CHANGE_TYPE.PHASE_COMPLETED:
      return `Completed phase: ${metadata?.phaseName || 'Unknown'}`;
    case SITE_HISTORY_CHANGE_TYPE.PHASE_SKIPPED:
      return `Skipped phase: ${metadata?.phaseName || 'Unknown'}`;
    case SITE_HISTORY_CHANGE_TYPE.PHASE_ADDED:
      return `Added new phase: ${metadata?.phaseName || 'Unknown'}`;
    case SITE_HISTORY_CHANGE_TYPE.PHASE_REMOVED:
      return `Removed phase: ${metadata?.phaseName || 'Unknown'}`;
    case SITE_HISTORY_CHANGE_TYPE.PHASE_PROGRESS_UPDATED:
      return `Updated progress for ${metadata?.phaseName || 'phase'}: ${oldValue || 0}% → ${newValue}%`;
    case SITE_HISTORY_CHANGE_TYPE.TASK_CREATED:
      return `Created task: "${metadata?.taskTitle || 'Unknown'}"`;
    case SITE_HISTORY_CHANGE_TYPE.TASK_COMPLETED:
      return `Completed task: "${metadata?.taskTitle || 'Unknown'}"`;
    case SITE_HISTORY_CHANGE_TYPE.TASK_DELETED:
      return `Deleted task: "${metadata?.taskTitle || 'Unknown'}"`;
    default:
      return item.description || SITE_HISTORY_CHANGE_TYPE_LABELS[changeType] || changeType;
  }
};

const SiteHistoryTimeline = ({
  history = [],
  loading = false,
  maxItems = 10,
  showAll = false,
  onShowMore
}) => {
  const [filter, setFilter] = useState('all');

  // Filter options
  const filterOptions = [
    { value: 'all', label: 'All' },
    { value: 'phases', label: 'Phases' },
    { value: 'tasks', label: 'Tasks' },
    { value: 'status', label: 'Status' }
  ];

  // Filter history items
  const filteredHistory = history.filter(item => {
    if (filter === 'all') return true;
    if (filter === 'phases') {
      return [
        SITE_HISTORY_CHANGE_TYPE.PHASE_STARTED,
        SITE_HISTORY_CHANGE_TYPE.PHASE_COMPLETED,
        SITE_HISTORY_CHANGE_TYPE.PHASE_SKIPPED,
        SITE_HISTORY_CHANGE_TYPE.PHASE_ADDED,
        SITE_HISTORY_CHANGE_TYPE.PHASE_REMOVED,
        SITE_HISTORY_CHANGE_TYPE.PHASE_PROGRESS_UPDATED
      ].includes(item.changeType);
    }
    if (filter === 'tasks') {
      return [
        SITE_HISTORY_CHANGE_TYPE.TASK_CREATED,
        SITE_HISTORY_CHANGE_TYPE.TASK_COMPLETED,
        SITE_HISTORY_CHANGE_TYPE.TASK_DELETED
      ].includes(item.changeType);
    }
    if (filter === 'status') {
      return item.changeType === SITE_HISTORY_CHANGE_TYPE.STATUS_CHANGED;
    }
    return true;
  });

  // Sort by timestamp (most recent first)
  const sortedHistory = [...filteredHistory].sort(
    (a, b) => new Date(b.timestamp || b.createdAt) - new Date(a.timestamp || a.createdAt)
  );

  // Limit display
  const displayHistory = showAll ? sortedHistory : sortedHistory.slice(0, maxItems);

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
          {[1, 2, 3].map(i => (
            <div key={i} className="flex gap-3 mb-4">
              <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
              <div className="flex-1">
                <div className="h-3 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Activity History</h3>
        <span className="text-sm text-gray-500">{filteredHistory.length} entries</span>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-1 mb-4 bg-gray-100 p-1 rounded-lg">
        {filterOptions.map(option => (
          <button
            key={option.value}
            onClick={() => setFilter(option.value)}
            className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
              filter === option.value
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* Timeline */}
      {displayHistory.length > 0 ? (
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>

          {/* Timeline items */}
          <div className="space-y-4">
            {displayHistory.map((item, index) => (
              <div key={item.id || index} className="relative flex gap-4">
                {/* Icon */}
                <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${getChangeTypeColor(item.changeType)}`}>
                  {getChangeTypeIcon(item.changeType)}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pb-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm text-gray-900">
                        {formatDescription(item)}
                      </p>
                      {item.metadata?.notes && (
                        <p className="text-xs text-gray-500 mt-1 italic">
                          "{item.metadata.notes}"
                        </p>
                      )}
                      {item.metadata?.reason && (
                        <p className="text-xs text-gray-500 mt-1 italic">
                          Reason: {item.metadata.reason}
                        </p>
                      )}
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap">
                      {formatTimestamp(item.timestamp || item.createdAt)}
                    </span>
                  </div>
                  {/* User info */}
                  {item.changedByUser && (
                    <p className="text-xs text-gray-400 mt-1">
                      by {item.changedByUser.nickname || item.changedByUser.fullName || 'Unknown'}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center py-8">
          <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-gray-500">No activity history yet</p>
        </div>
      )}

      {/* Show More Button */}
      {!showAll && sortedHistory.length > maxItems && (
        <button
          onClick={onShowMore}
          className="w-full mt-4 py-2 text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center justify-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
          Show More ({sortedHistory.length - maxItems} more)
        </button>
      )}
    </div>
  );
};

export default SiteHistoryTimeline;
